import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';

type BoundaryCutEndpointKind = 'start' | 'end';

const ENDPOINT_APPEARANCE: Record<BoundaryCutEndpointKind, { readonly color: string; readonly label: string }> = {
  start: { color: '#163f52', label: 'A · Inicio' },
  end: { color: '#e36a16', label: 'B · Final' },
};

const endpointStyle = (feature: FeatureLike): Style => {
  const kind = feature.get('boundaryCutEndpoint') as BoundaryCutEndpointKind;
  const appearance = ENDPOINT_APPEARANCE[kind];
  return new Style({
    image: new CircleStyle({
      radius: 8,
      fill: new Fill({ color: appearance.color }),
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
    text: new Text({
      text: appearance.label,
      offsetY: -20,
      font: '600 12px system-ui, sans-serif',
      fill: new Fill({ color: '#102b36' }),
      backgroundFill: new Fill({ color: 'rgba(255,255,255,.94)' }),
      backgroundStroke: new Stroke({ color: appearance.color, width: 1 }),
      padding: [3, 5, 3, 5],
    }),
  });
};

/** Temporary map-only markers for the snapped endpoints of a boundary cut. */
export class BoundaryCutEndpointMarkers {
  readonly source = new VectorSource<Feature<Point>>({ wrapX: false });
  readonly layer = new VectorLayer({
    source: this.source,
    style: endpointStyle,
    properties: { role: 'boundary-cut-endpoints' },
  });

  set(start: readonly number[] | null, end: readonly number[] | null): void {
    this.source.clear(true);
    if (start) this.source.addFeature(this.feature('start', start));
    if (end) this.source.addFeature(this.feature('end', end));
  }

  clear(): void {
    this.source.clear(true);
  }

  private feature(kind: BoundaryCutEndpointKind, coordinate: readonly number[]): Feature<Point> {
    return new Feature({ geometry: new Point([...coordinate]), boundaryCutEndpoint: kind });
  }
}
